import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
// antd components
import { Layout, Button, message, Breadcrumb } from 'design-system';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import { useDepartmentsByPrivilege } from 'src/hooks/use-departments-by-privilege';

import { getAllUsers } from 'src/services/user';
import { PrivilegeType } from 'src/services/role';
import { queryKeys } from 'src/constants/queryKeys';
import type { ActionPlan, FilterActionPlan } from 'src/services/actionPlan';
import {
  getActionPlan,
  deleteActionPlan,
  getAllActionPlans,
  filterActionPlans,
} from 'src/services/actionPlan';

import StatisticsCards from 'src/components/statistics-cards';

import { paths } from '../../../routes/paths';
import ActionPlanEditor from '../action-plan-editor';
import ActionPlanDetail from '../action-plan-detail';
import { UseGetReFetchEnable } from '../../../utils';
import ActionPlanListTable from '../action-plan-list-table';
import ActionPlanCreateDrawer from './action-plan-create-drawer';

const { Content } = Layout;

type EditorAction = 'create' | 'edit';

export default function ActionPlanListView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const enabled = UseGetReFetchEnable(
    paths.dashboard.improvementProposal.actionPlanManagement.root
  );
  const queryClient = useQueryClient();
  const [openEditor, setOpenEditor] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editorAction, setEditorAction] = useState<EditorAction>('create');
  const [selectedPlan, setSelectedPlan] = useState<ActionPlan | null>(null);
  // 从URL参数中读取分页信息
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const sizeParam = searchParams.get('size');
    return sizeParam ? parseInt(sizeParam, 10) : 10;
  });
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterParams, setFilterParams] = useState<FilterActionPlan | null>(null);

  // 获取用户和部门数据
  const { data: users = [] } = useQuery({
    queryKey: [queryKeys.getAllUsers],
    queryFn: () => getAllUsers(),
    staleTime: 0,
    enabled,
  });

  const { departmentOptions } = useDepartmentsByPrivilege(PrivilegeType.ACTION_PLAN, {
    staleTime: 0,
    enabled,
  });

  // 处理URL参数中的departmentId
  useEffect(() => {
    const departmentIds = searchParams.getAll('departmentId');

    if (departmentIds.length > 0) {
      // 设置过滤参数，只使用departmentIds数组
      const filter: FilterActionPlan = {
        departmentIds,
      };

      // 不重置页码，保持当前分页状态
      handleFilter(filter, false);
    }
  }, [searchParams]);

  // 监听URL参数变化，同步分页状态
  useEffect(() => {
    const pageParam = searchParams.get('page');
    const sizeParam = searchParams.get('size');

    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (page !== currentPage) {
        setCurrentPage(page);
      }
    }

    if (sizeParam) {
      const size = parseInt(sizeParam, 10);
      if (size !== pageSize) {
        setPageSize(size);
      }
    }
  }, [searchParams, currentPage, pageSize]);

  // 如果有id参数，获取对应的行动计划
  useEffect(() => {
    if (id) {
      const fetchActionPlan = async () => {
        try {
          const plan = await getActionPlan(id);
          setSelectedPlan(plan);
          setOpenDetail(true);
        } catch (error) {
          // 错误处理
        }
      };
      fetchActionPlan();
    }
  }, [id]);

  // 获取所有行动计划或根据过滤条件获取
  const { data: plansData, isLoading: loadingAllPlans } = useQuery({
    queryKey: [queryKeys.getAllActionPlans, currentPage, pageSize, isFiltering, filterParams],
    queryFn: async () => {
      if (isFiltering && filterParams) {
        // 使用过滤API获取数据
        return filterActionPlans(filterParams, {
          page: currentPage,
          size: pageSize,
        });
      }
      // 获取所有数据
      return getAllActionPlans({ page: currentPage, size: pageSize });
    },
    enabled,
    staleTime: 0, // 5分钟
  });

  const handleOpenEditor = async (action: EditorAction, plan?: ActionPlan) => {
    setEditorAction(action);
    if (action === 'edit' && plan?.id) {
      try {
        const latestPlan = await getActionPlan(plan.id);
        setSelectedPlan(latestPlan);
      } catch (error) {
        // 错误处理
        setSelectedPlan(plan || null);
      }
    } else {
      setSelectedPlan(plan || null);
    }
    setOpenEditor(true);
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const handleSaveActionPlans = () => {
    // 新的Drawer组件会自动处理创建和刷新，这里只需要刷新列表数据
    queryClient.invalidateQueries([queryKeys.getAllActionPlans]);
    setOpenDrawer(false);
  };

  const handleOpenDetail = async (plan: ActionPlan) => {
    if (!plan.id) {
      setSelectedPlan(plan);
      setOpenDetail(true);
      return;
    }

    try {
      // 通过getActionPlan获取最新数据
      const latestPlan = await getActionPlan(plan.id);
      setSelectedPlan(latestPlan);
      setOpenDetail(true);
    } catch (error) {
      // 错误处理
      // 如果获取失败，仍然使用原有数据
      setSelectedPlan(plan);
      setOpenDetail(true);
    }
  };

  const handleCloseEditor = () => {
    // 关闭编辑器后立即刷新数据
    queryClient.invalidateQueries([queryKeys.getAllActionPlans]);
    setOpenEditor(false);
    setSelectedPlan(null);
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelectedPlan(null);
  };

  const handleDelete = async (record: ActionPlan) => {
    try {
      if (!record.id) {
        enqueueSnackbar('无法删除：行动计划ID不存在', { variant: 'error' });
        return;
      }

      await deleteActionPlan(record.id);

      message.alert({ content: '行动计划删除成功', type: 'success' });

      // 刷新列表数据
      queryClient.invalidateQueries([queryKeys.getAllActionPlans]);
    } catch (error) {
      enqueueSnackbar('删除行动计划失败，请重试', { variant: 'error' });
    }
  };

  const handleFilter = (filter: FilterActionPlan | null, resetPage = true) => {
    setFilterParams(filter);
    setIsFiltering(!!filter);
    if (resetPage) {
      setCurrentPage(1);
    }
  };

  const handleResetFilter = () => {
    setFilterParams(null);
    setIsFiltering(false);

    // 清除URL中的所有查询参数
    navigate(window.location.pathname, { replace: true });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    // 同步更新URL参数，保持departmentId等参数
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page.toString());
    navigate({ search: newSearchParams.toString() }, { replace: true });
  };

  const handleShowSizeChange = (current: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1);

    // 同步更新URL参数
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('size', size.toString());
    newSearchParams.set('page', '1');
    navigate({ search: newSearchParams.toString() }, { replace: true });
  };

  const handleViewCalendar = () => {
    navigate('/dashboard/action-plan/calendar');
  };

  return (
    <Content style={{ minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Breadcrumb
            items={[
              {
                title: '行动计划管理',
              },
            ]}
          />
          <div>
            <Button type="primary" onClick={handleOpenDrawer}>
              新建行动计划
            </Button>
          </div>
        </div>
      </div>

      <ActionPlanListTable
        enabled={enabled}
        data={plansData?.records || []}
        loading={loadingAllPlans}
        total={plansData?.total || 0}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onShowSizeChange={handleShowSizeChange}
        onEdit={async (record: ActionPlan) => handleOpenEditor('edit', record)}
        onView={(record: ActionPlan) => handleOpenDetail(record)}
        onDelete={handleDelete}
        onViewCalendar={handleViewCalendar}
        onFilter={handleFilter}
        onResetFilter={handleResetFilter}
        initialFilter={filterParams}
        slot={
          plansData?.statistic ? (
            <StatisticsCards
              title="行动计划总数"
              totalValue={plansData.statistic.total || 0}
              items={[
                {
                  label: '已完成',
                  value: plansData.statistic.completed || 0,
                  color: '#3AC97B',
                  type: 'progress',
                },
                {
                  label: '未完成',
                  value: plansData.statistic.notStarted || 0,
                  color: '#FFB020',
                  type: 'progress',
                },

                // {
                //   label: '已取消',
                //   value: plansData.statistic.canceled || 0,
                //   color: '#FF5B5B',
                //   type: 'progress',
                // },
              ]}
            />
          ) : undefined
        }
      />

      <ActionPlanEditor
        open={openEditor}
        onClose={handleCloseEditor}
        action={editorAction}
        data={selectedPlan}
      />

      <ActionPlanCreateDrawer
        open={openDrawer}
        onClose={handleCloseDrawer}
        users={users}
        departments={departmentOptions}
        onSuccess={handleSaveActionPlans}
      />

      <ActionPlanDetail open={openDetail} onClose={handleCloseDetail} data={selectedPlan} />
    </Content>
  );
}
